import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18next';
import { ONE_HUNDRED_PERCENT } from 'utils/constants/sportsbook/magic-numbers';
import { EBetBalanceType } from 'interfaces/betslip';
import { calculateAndDispatchUpdates } from 'newelements/_Betslip/utils/action-middlewares/store-actions-middlewares/calculateAndDispatchUpdates';
import { CustomMenu } from 'components/UI/CustomMenu';
import { useProfitBoost } from 'hooks/useProfitBoost';
import {
  setIsFrenchFreeBetEnabled,
  setUseBonusBalance
} from 'store/actions/betslip';
import {
  getCounterOfferActiveEventIds,
  getIsFreeBetEnabled,
  getIsFrenchFreeBetEnabled,
  getProfitBoost,
  getProfitBoostAdvanced,
  getSuperBetActiveEventIds
} from 'store/selectors/betslip';
import { getUser } from 'store/selectors/user-data';
import './index.less';

export const BetWithBonusOrDepositBalance = (): ReactElement => {
  const [selectedType, setSelectedType] = useState<EBetBalanceType>(
    EBetBalanceType.REAL
  );

  const superBetActiveEventIds = useSelector(getSuperBetActiveEventIds);
  const counterOfferActiveEventIds = useSelector(getCounterOfferActiveEventIds);
  const isFrenchFreeBetEnabled = useSelector(getIsFrenchFreeBetEnabled);
  const profitBoost = useSelector(getProfitBoost);
  const profitBoostAdvanced = useSelector(getProfitBoostAdvanced);
  const user = useSelector(getUser);
  const isFreeBetEnabled = useSelector(getIsFreeBetEnabled);

  const dispatch = useDispatch();

  const { setToggleProfitBoost } = useProfitBoost();

  const isProfitBoostActive = !!(
    profitBoost?.selected || profitBoostAdvanced?.single?.length
  );

  const isSuperBetActive = !!superBetActiveEventIds.length;
  const isCounterOfferActive = !!counterOfferActiveEventIds.length;

  const menuItems = useMemo(() => {
    const result = [
      {
        value: i18n.t('betslip.realMode'),
        key: EBetBalanceType.REAL
      }
    ];

    if (user.sport_bonus) {
      result.push({
        value: i18n.t('betslip.bonusMode'),
        key: EBetBalanceType.BONUS
      });
    }

    if (user.bonus_money) {
      result.push({
        value: i18n.t('betslip.frenchFreeBet'),
        key: EBetBalanceType.FREE_BET
      });
    }

    return result;
  }, [user.sport_bonus, user.bonus_money]);

  const handleSelectTab = (key: EBetBalanceType) => {
    setSelectedType(key);

    dispatch(setUseBonusBalance(key === EBetBalanceType.BONUS));
    dispatch(setIsFrenchFreeBetEnabled(key === EBetBalanceType.FREE_BET));

    // TODO: Nahepet-sb need to review this functionality
    if (key === EBetBalanceType.FREE_BET) {
      if (isProfitBoostActive) {
        setToggleProfitBoost();
      }

      if (isCounterOfferActive) {
        counterOfferActiveEventIds.forEach(id =>
          calculateAndDispatchUpdates({ id, oddOffer: null })
        );
      }

      if (isSuperBetActive) {
        superBetActiveEventIds.forEach(id =>
          calculateAndDispatchUpdates({ id, superBet: false })
        );
      }
    }
  };

  useEffect(() => {
    if (selectedType !== EBetBalanceType.BONUS) {
      dispatch(setUseBonusBalance(false));
    }

    if (selectedType !== EBetBalanceType.FREE_BET) {
      dispatch(setIsFrenchFreeBetEnabled(false));
    }

    return () => {
      dispatch(setUseBonusBalance(false));
      dispatch(setIsFrenchFreeBetEnabled(false));
    };
  }, []);

  useEffect(() => {
    if (isFrenchFreeBetEnabled) {
      setSelectedType(EBetBalanceType.FREE_BET);
    } else if (selectedType === EBetBalanceType.BONUS) {
      setSelectedType(EBetBalanceType.BONUS);
    } else {
      setSelectedType(EBetBalanceType.REAL);
    }
  }, [isFrenchFreeBetEnabled]);

  // TODO: @Nahept-sb need to review this functionality, this is for switch freebet tab on selecting other betslip feature
  useEffect(() => {
    if (
      (isProfitBoostActive || isCounterOfferActive || isSuperBetActive) &&
      isFrenchFreeBetEnabled
    ) {
      setSelectedType(EBetBalanceType.REAL);
      dispatch(setIsFrenchFreeBetEnabled(false));
    }
  }, [isSuperBetActive, isCounterOfferActive, isProfitBoostActive]);

  return (
    <div className="user__balanceOrDeposit__balance">
      <CustomMenu
        disabled={isFreeBetEnabled}
        items={menuItems}
        activeItem={selectedType}
        setActiveItem={handleSelectTab}
        itemStyles={{ width: `${ONE_HUNDRED_PERCENT / menuItems.length}%` }}
      />
    </div>
  );
};
